import { useState } from 'react';

import { semverCompare } from '@/react/common/semver-utils';
import { useRegistry } from '@/react/portainer/registries/queries/useRegistry';
import { HelmRelease } from '@/react/kubernetes/helm/types';
import { useHelmRepoVersions } from '@/react/kubernetes/helm/helmChartSourceQueries/useHelmRepoVersions';

import { Button } from '@@/buttons';
import { InlineLoader } from '@@/InlineLoader';
import { Tooltip } from '@@/Tip/Tooltip';

export function VersionStatusMessage({ release }: { release: HelmRelease }) {
  const [useCache, setUseCache] = useState(true);
  const registryQuery = useRegistry(release?.chartReference?.registryID);
  const releaseChartVersion = release.chart.metadata?.version;

  const {
    isError,
    // use 'isFetching' to display a loading state when clicking refresh
    isFetching,
    data: versions,
    refetch: refetchVersions,
  } = useHelmRepoVersions(
    {
      staleTime: 60 * 60 * 1000, // 1 hour
    },
    release.chartReference,
    useCache
  );
  const latestVersionAvailable = versions?.[0]?.version ?? '';
  const isNewVersionAvailable =
    !!releaseChartVersion &&
    semverCompare(latestVersionAvailable, releaseChartVersion) === 1;
  const registry = registryQuery.data;
  const registryUrl = registry?.URL ?? release?.chartReference?.repoURL;
  const isChartReferenceExists =
    !!release?.chartReference?.chartPath &&
    (!!release?.chartReference?.repoURL ||
      !!release?.chartReference?.registryID);
  const isGitManaged = !!release?.stackID;
  const hasNoAvailableVersions = !versions?.length && !isGitManaged; // undefined or 0

  if (isError) {
    return null;
  }

  if (isFetching) {
    return (
      <InlineLoader
        size="xs"
        className="absolute right-0 -bottom-5 left-0 whitespace-nowrap"
      >
        Checking for new versions...
      </InlineLoader>
    );
  }

  return (
    <span className="text-muted absolute right-0 -bottom-5 left-0 flex items-center text-center text-xs whitespace-nowrap">
      {getStatusText(
        hasNoAvailableVersions,
        latestVersionAvailable,
        isNewVersionAvailable,
        isGitManaged
      )}
      {hasNoAvailableVersions && (
        <Tooltip
          message={
            isChartReferenceExists
              ? `Portainer can't find the Helm chart in '${registryUrl}'. You can set it while upgrading the release.`
              : `Portainer can't detect the Helm chart for '${release.name}'. You can set it while upgrading the release.`
          }
        />
      )}
      {isChartReferenceExists && (
        <Button
          data-cy="k8sApp-refreshHelmChartVersionsButton"
          color="link"
          size="xsmall"
          onClick={handleRefreshVersions}
          type="button"
        >
          Refresh
        </Button>
      )}
    </span>
  );

  function handleRefreshVersions() {
    if (useCache) {
      // clicking 'refresh versions' should get the latest versions from the repo, not the cached versions
      setUseCache(false);
    }
    refetchVersions();
  }

  function getStatusText(
    hasNoAvailableVersions: boolean,
    latestVersionAvailable: string,
    isNewVersionAvailable: boolean,
    isGitManaged: boolean
  ) {
    if (isGitManaged) {
      return '';
    }
    if (hasNoAvailableVersions) {
      return 'No versions available ';
    }
    if (isNewVersionAvailable) {
      return `New version available (${latestVersionAvailable}) `;
    }
    return 'Latest version installed';
  }
}
